import 'react';
import { useTranslation } from 'react-i18next';
import { PlusOutlined } from '@ant-design/icons';
import { Button, Checkbox, Form, Input } from 'antd';
import { TrashIcon } from '@/assets/icons';
import { cn } from '@/libs/utils';

const generateSlug = (label) => {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '_')
    .replace(/-+/g, '_')
    .substring(0, 50);
};

export default function VariationTypesForm({
  name,
  form,
  disabled = false,
  showIncludeInDefault = false,
}) {
  const { t } = useTranslation(['forms']);

  return (
    <Form.List name={name}>
      {(fields, { add, remove }, { errors }) => (
        <div className="space-y-2">
          {fields.map(({ key, name: fieldName, ...restField }) => (
            <div key={key} className="flex items-start gap-2">
              <Form.Item
                {...restField}
                name={[fieldName, 'label']}
                rules={[
                  {
                    required: true,
                    message: t('formValidation.required', { ns: 'forms' }),
                  },
                ]}
                className="mb-0 flex-grow"
                getValueFromEvent={(e) => {
                  const label = e.target.value;
                  const key = generateSlug(label);
                  // Update the key field when label changes
                  const currentTypes = form.getFieldValue(name) || [];
                  if (currentTypes[fieldName]) {
                    currentTypes[fieldName].key = key;
                    form.setFieldsValue({
                      [name[0]]: { [name[1]]: currentTypes },
                    });
                  }
                  return label;
                }}
              >
                <Input
                  placeholder={t('variationTypePlaceholder', { ns: 'forms' })}
                  disabled={disabled}
                />
              </Form.Item>

              <Form.Item
                {...restField}
                name={[fieldName, 'key']}
                className="mb-0 hidden"
              >
                <Input type="hidden" />
              </Form.Item>

              {showIncludeInDefault && (
                <Form.Item
                  {...restField}
                  name={[fieldName, 'includeInDefault']}
                  valuePropName="checked"
                  className="mb-0"
                >
                  <Checkbox disabled={disabled}>
                    {t('includeInDefaultExport', { ns: 'forms' })}
                  </Checkbox>
                </Form.Item>
              )}

              <Button
                type="text"
                title={t('removeVariationType', { ns: 'forms' })}
                onClick={() => remove(fieldName)}
                icon={<TrashIcon />}
                disabled={disabled}
                className={cn(
                  'btn-default-action h-8 w-8 !bg-red-500 text-white hover:!text-white'
                )}
              >
                <span className="sr-only">
                  {t('removeVariationType', { ns: 'forms' })}
                </span>
              </Button>
            </div>
          ))}

          <Button
            type="button"
            onClick={() => add({ key: '', label: '', includeInDefault: true })}
            className="btn-default w-full rounded-[4px] border !border-zinc-200 font-semibold"
            icon={<PlusOutlined />}
            disabled={disabled}
          >
            {t('addVariationType', { ns: 'forms' })}
          </Button>

          <Form.ErrorList errors={errors} />
        </div>
      )}
    </Form.List>
  );
}
